import './AppliedFilters.scss';
import { useCallback, useMemo } from 'react';
import { Button, Chip } from '@progress/kendo-react-buttons';
import { classNames } from '@progress/kendo-react-common';

import { useAppDispatch, useAppSelector } from '../../../hooks/redux';
import {
  clearFilters,
  removeAppliedFilter,
  selectAppliedFilters,
} from '../../../store/slices/common-filters.slice';
import {
  AutocompleteFilterOption,
  CommonFilterObject,
  CommonFiltersItem,
  DatasetIDComponentProp,
} from '../../../models/filter.model';
import {
  selectVisualType,
  selectVisualData,
  selectStartDate,
  selectEndDate,
  resetDateFilters,
  selectPlanningVisualType,
  selectPlanningStartDate,
  selectPlanningEndDate,
  selectPlanningVisualData,
} from '../../../store/slices/date-filter.slice';
import { KWELLO_ICON, KwelloSvgIcon } from '../../../components/icon/KwelloSvgIcon';
import { DATASET_INITIAL_ID } from '../../../constants';
import { toggleFilters } from '../../../store/slices/filter-builder.slice';
import {
  clearChartFilters,
  removeChartFilter,
  selectChartFilters,
} from '../../../store/slices/chart-filters.slice';
import { RootState } from '../../../store';
import {
  selectSavedFilterKey,
  selectSavedFiltersEntries,
  setSavedFilterKey,
} from '../../../store/slices/saved-filters.slice';
import { Tooltip } from 'react-tooltip';
import { formatFilterDateString, onlyUnique, truncateString } from '../../../hooks/utils';
import { useChartFiltersAsPageFilters } from '../../../hooks/filters';
import usePageFilters from 'common/hooks/usePageFilters';
import { getFilterConfig } from 'modules/insights/constants/constants-filter-configs';
import { useCompareUrlWithProjectId } from 'common/hooks/route';
import { PLANNING_ROUTES } from 'routes/routes.planning.const';

interface GenerateFilterLabelArgs {
  filterKey: string;
  filter: CommonFilterObject;
}

const generateFilterLabel = ({ filterKey, filter }: GenerateFilterLabelArgs) => {
  if (filter?.label) {
    if (filter.label === 'Category/Question/Response') {
      return (filter?.value?.[0].label as string) ?? (filter?.value?.[0].filterType as string);
    }
  }

  const filterConfigLabel = getFilterConfig(filterKey)?.label;

  return filterConfigLabel ?? filter?.label;
};

export const chipTextArray = (filter: AutocompleteFilterOption[], expand = false): string[] => {
  if (!filter) return [];
  const uniqueOptions = filter.filter((option, index, array) => {
    return array.findIndex((item) => item.name === option.name) === index;
  });

  if (uniqueOptions.length <= 2 && !expand) {
    return uniqueOptions.map((option: AutocompleteFilterOption) => option.name);
  }
  const valueClone = [...uniqueOptions];
  const result: string[] = valueClone
    .splice(0, expand ? valueClone.length : 2)
    .map((option: AutocompleteFilterOption) => option.name);
  if (!expand) {
    result.push('+' + valueClone.length + ' more');
  }
  return result;
};

export function AppliedFilters({
  datasetId = DATASET_INITIAL_ID,
  showTooltip = true,
  removable = true,
  textClass = 'k-color-primary',
}: Readonly<DatasetIDComponentProp>) {
  const { compareUrl } = useCompareUrlWithProjectId();
  const displayDateType = useAppSelector((root: RootState) => selectVisualType(root, datasetId));
  const displayPlanningDateType = useAppSelector((root: RootState) =>
    selectPlanningVisualType(root, datasetId)
  );
  const displayPlanningDateVisualData = useAppSelector((root: RootState) =>
    selectPlanningVisualData(root, datasetId)
  );
  const planningStartDate = useAppSelector((root: RootState) =>
    selectPlanningStartDate(root, datasetId)
  );
  const planningEndDate = useAppSelector((root: RootState) =>
    selectPlanningEndDate(root, datasetId)
  );
  const displayDateVisualData = useAppSelector((root: RootState) =>
    selectVisualData(root, datasetId)
  );
  const startDate = useAppSelector((root: RootState) => selectStartDate(root, datasetId));
  const endDate = useAppSelector((root: RootState) => selectEndDate(root, datasetId));
  const { pageFilterKeys } = usePageFilters({ datasetId });

  const selectedSavedFilter = useAppSelector((root: RootState) =>
    selectSavedFilterKey(root, datasetId)
  );
  const savedFilters = useAppSelector(selectSavedFiltersEntries);
  const filters = useAppSelector((root: RootState) => selectAppliedFilters(root, datasetId));
  const chartFilters = useAppSelector((root: RootState) => selectChartFilters(root, datasetId));
  const isPlanningPage = compareUrl(PLANNING_ROUTES.root, { includes: true });
  const dispatch = useAppDispatch();

  const convertedChartFilters = useChartFiltersAsPageFilters(datasetId);

  const filterKeys = Array.from(
    new Set([...Object.keys(filters), ...Object.keys(convertedChartFilters)])
  );

  const mergedFilters = useMemo(() => {
    return filterKeys.reduce((acc: CommonFiltersItem, key: string) => {
      const pageFilter = filters[key];
      const chartFilter = convertedChartFilters[key];

      if (pageFilter && chartFilter) {
        acc[key] = {
          label: generateFilterLabel({ filterKey: key, filter: pageFilter }),
          chartFilterId: chartFilter.chartFilterId,
          value: [...pageFilter.value, ...chartFilter.value].reduce(
            (acc, value) => {
              if (!acc.find((v: any) => v.name === value.name)) {
                acc.push(value);
              }

              // This is because filter values are hard typed to any ¯\_(ツ)_/¯
              // eslint-disable-next-line @typescript-eslint/no-unsafe-return
              return acc;
            },
            [] as { name: string; value: string }[]
          ),
        };
      } else if (pageFilter) {
        acc[key] = {
          ...pageFilter,
          label: generateFilterLabel({ filterKey: key, filter: pageFilter }),
        };
      } else if (chartFilter) {
        acc[key] = {
          ...chartFilter,
          label: generateFilterLabel({ filterKey: key, filter: chartFilter }),
        };
      }

      return acc;
    }, {});
  }, [filters, convertedChartFilters, filterKeys]);

  const keys = useMemo(
    () =>
      [
        ...Object.keys(filters || {}),
        ...chartFilters.map((chartFilter) => chartFilter.propertyName),
      ].filter(onlyUnique),
    [chartFilters, filters]
  );

  const resetFilters = useCallback(() => {
    dispatch(clearFilters({ datasetId }));
    dispatch(clearChartFilters({ datasetId }));
    dispatch(resetDateFilters({ datasetId }));
    dispatch(setSavedFilterKey({ datasetId, value: undefined }));
  }, [datasetId, dispatch]);

  const openFilters = useCallback(() => {
    dispatch(toggleFilters({ visible: true, datasetId }));
  }, [datasetId, dispatch]);

  const removeFilter = useCallback(
    (filterKey: string, chartFilterId?: string) => {
      dispatch(
        removeAppliedFilter({
          datasetId,
          filterKey,
        })
      );

      if (chartFilterId) {
        const chartFiltersToRemove = chartFilters.filter(
          (chartFilter) => chartFilter.propertyName === chartFilterId
        );

        chartFiltersToRemove.forEach((chartFilter) => {
          dispatch(
            removeChartFilter({
              index: 0,
              datasetId,
              propertyName: chartFilter.propertyName,
              value: chartFilter.value,
            })
          );
        });
      }
    },
    [chartFilters, datasetId, dispatch]
  );

  const dateString = useMemo(() => {
    return formatFilterDateString({
      displayDateType,
      startDate,
      endDate,
      visualData: displayDateVisualData,
    });
  }, [startDate, endDate, displayDateType, displayDateVisualData]);

  const planningDateString = useMemo(() => {
    return formatFilterDateString({
      displayDateType: displayPlanningDateType,
      startDate: planningStartDate,
      endDate: planningEndDate,
      visualData: displayPlanningDateVisualData,
    });
  }, [displayPlanningDateType, displayPlanningDateVisualData, planningStartDate, planningEndDate]);

  const appliedSavedFilter = useMemo(() => {
    return savedFilters?.find((filter) => filter.key === selectedSavedFilter);
  }, [savedFilters, selectedSavedFilter]);

  const unappliedSavedFiltersKeys = useMemo(() => {
    if (appliedSavedFilter?.value.filters) {
      return Object.keys(appliedSavedFilter?.value.filters).filter(
        (k) => keys.findIndex((key) => key === k) === -1
      );
    }
    return [];
  }, [appliedSavedFilter, keys]);

  return (
    <div className="applied-filters">
      <div className="applied-filters-chip-list">
        <div className="applied-filter-children" title={dateString}>
          {appliedSavedFilter && (
            <Chip
              rounded={'large'}
              removable={removable}
              fillMode={'outline'}
              onRemove={resetFilters}
            >
              <span className="k-chip-label">Saved Search&nbsp;</span>
              <b className={textClass}>{appliedSavedFilter.value.name}</b>
            </Chip>
          )}
          {!appliedSavedFilter && isPlanningPage && (
            <Chip rounded={'large'} fillMode={'outline'} onClick={() => openFilters()}>
              <>
                <KwelloSvgIcon
                  icon={KWELLO_ICON.CALENDAR_ICON}
                  className="k-icon k-svg-icon k-button-icon"
                />
                <span className="k-chip-label">
                  <span>Date</span> <b className={textClass}>{planningDateString}</b>
                </span>
              </>
            </Chip>
          )}
          {!appliedSavedFilter && !isPlanningPage && (
            <Chip rounded={'large'} fillMode={'outline'} onClick={() => openFilters()}>
              <>
                <KwelloSvgIcon
                  icon={KWELLO_ICON.CALENDAR_ICON}
                  className="k-icon k-svg-icon k-button-icon"
                />
                <span className="k-chip-label">
                  <span>Date</span> <b className={textClass}>{dateString}</b>
                </span>
              </>
            </Chip>
          )}
        </div>
        {appliedSavedFilter && (
          <div className="applied-filter-children applied-saved-filter">
            <Chip rounded={'large'} fillMode={'outline'} onClick={() => openFilters()}>
              <>
                <KwelloSvgIcon
                  icon={KWELLO_ICON.CALENDAR_ICON}
                  className="k-icon k-svg-icon k-button-icon"
                />
                <span className="k-chip-label">
                  <span>Date</span> <b>{dateString}</b>
                </span>
              </>
            </Chip>
          </div>
        )}

        {filterKeys?.map((filterKey: string) => {
          const currentFilter = mergedFilters[filterKey];
          const textArray = chipTextArray(mergedFilters[filterKey]?.value);
          const expanded = chipTextArray(mergedFilters[filterKey]?.value, true);

          return (
            <div
              data-tooltip-id={'filter-chip-' + filterKey}
              key={filterKey}
              className="applied-filter-children"
            >
              <Chip
                rounded={'large'}
                disabled={!pageFilterKeys.includes(filterKey)}
                removable={pageFilterKeys.includes(filterKey) && removable && !appliedSavedFilter}
                fillMode={'outline'}
                onRemove={(e) => {
                  e.syntheticEvent.stopPropagation();
                  e.syntheticEvent.preventDefault();

                  removeFilter(filterKey, currentFilter.chartFilterId);
                }}
                onClick={openFilters}
              >
                <span className="k-chip-label">{currentFilter?.label}&nbsp;</span>
                <b className={classNames({ [textClass]: !appliedSavedFilter })}>
                  {textArray
                    .map((text) => truncateString(text))
                    .join(textArray.length > 1 ? ', ' : ' ')}
                </b>
              </Chip>

              {showTooltip && (
                <Tooltip
                  id={'filter-chip-' + filterKey}
                  className="applied-filters-tooltip"
                  place="top"
                  positionStrategy={'fixed'}
                  style={{ fontSize: '0.8rem', maxWidth: 300, zIndex: 9999 }}
                >
                  {!pageFilterKeys.includes(filterKey) &&
                    'This filter attribute is not available on this page'}

                  {pageFilterKeys.includes(filterKey) && (
                    <ul>
                      {expanded.map((text) => (
                        <li key={text}>{text}</li>
                      ))}
                    </ul>
                  )}
                </Tooltip>
              )}
            </div>
          );
        })}

        {unappliedSavedFiltersKeys.map((filterKey: string) => {
          const currentFilter = appliedSavedFilter?.value.filters[filterKey];
          const textArray = chipTextArray(currentFilter?.value);

          return (
            <div
              key={filterKey}
              className="applied-filter-children"
              title={currentFilter?.value
                .map(({ name }: AutocompleteFilterOption) => name)
                .join(', ')}
            >
              <Chip
                rounded={'large'}
                disabled
                removable={removable && !appliedSavedFilter}
                fillMode={'outline'}
                onRemove={(e) => {
                  e.syntheticEvent.stopPropagation();
                  e.syntheticEvent.preventDefault();
                  removeFilter(filterKey);
                }}
                onClick={openFilters}
              >
                <span className="k-chip-label">{currentFilter?.label}&nbsp;</span>
                <span className={classNames({ [textClass]: !appliedSavedFilter })}>
                  {textArray.join(textArray.length > 1 ? ', ' : ' ')}
                </span>
              </Chip>
            </div>
          );
        })}
      </div>
      <div className="right">
        <Button onClick={() => resetFilters()} fillMode={'link'} size={'small'}>
          {appliedSavedFilter ? 'Reset' : 'Clear all'}
        </Button>
      </div>
    </div>
  );
}
